import {useCallback} from 'react';

import {createEECPurchaseEvent} from '@optimaxdev/analytics/desktop/lib/ecommerce/typ/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {convertPriceEEC} from '@optimaxdev/utils';
import {useSelector} from 'react-redux';

import {
    LOCAL_STORAGE_CHECKOUT,
    LOCAL_STORAGE_ORDER_DATA,
    PURCHASE_STORAGE,
    SESSION_STORAGE_POST_PURCHASE_ORDER_DATA,
} from 'constants/analytics';
import {getOrderId, getOrderLastData} from 'desktop/pages/success/store/selectors/selectors';
import {PostPurchaseOrderType} from 'desktop/pages/success/types/types';
import {useHostSelector} from 'hooks/hostHooks/hostHooks';
import {logWithSentry, logWithSentryEEC} from 'libs/sentry/sentry';
import {localStore, sessionStore} from 'libs/storage/storage';
import {getUrlParams} from 'libs/url/url';
import {getRouter} from 'selectors/router';

import {logWithSentryForErroneousRevenueOrOrder, sendEvent} from './helpers';

/**
 * Hook for sending EEC analytics
 */
export const useEcommerceAnalytics = () => {
    const {insurance} = getUrlParams();
    const id = useSelector(getOrderId);
    const orderLastData = useSelector(getOrderLastData);
    const router = useHostSelector(getRouter);
    const sku = (orderLastData?.items && orderLastData?.items[0]?.sku) || '';

    const sendEventForInsuranceOrder = useCallback(() => {
        return new Promise<void>(resolve => {
            try {
                const order = sessionStore.get(SESSION_STORAGE_POST_PURCHASE_ORDER_DATA);

                if (!order) {
                    logWithSentry(new Error('Analytics. EEC_purchase. No data on insurance order'));
                    return;
                }

                const {
                    ecommerce: {impressions: products},
                    originalOrderNumber,
                    subTotal,
                    taxTotal,
                } = JSON.parse(order) as PostPurchaseOrderType;
                pushDataLayer(
                    createEECPurchaseEvent({
                        actionField: {
                            action: 'purchase',
                            id,
                            revenue: String(subTotal),
                            tax: taxTotal.toFixed(4),
                        },
                        originalOrderNumber,
                        products: products.map(product => ({
                            ...product,
                            id: sku || product.id,
                        })),
                    }),
                );
                logWithSentryEEC('purchase', {
                    actionField: {
                        action: 'purchase',
                        id,
                        revenue: String(subTotal),
                        tax: taxTotal.toFixed(4),
                    },
                    originalOrderNumber,
                    products: products.map(product => ({
                        ...product,
                        id: sku || product.id,
                    })),
                });
                resolve();
            } catch (error) {
                logWithSentry(error);
            }
        });
    }, [id, sku]);

    const sendEventForRegularOrder = useCallback(() => {
        // eslint-disable-next-line max-statements
        return new Promise<void>(resolve => {
            try {
                const orderJSON = localStore.get(LOCAL_STORAGE_ORDER_DATA);
                const checkoutDataJSON = localStore.get(LOCAL_STORAGE_CHECKOUT);
                const placeOfInteraction = sessionStore.get(PURCHASE_STORAGE);

                const checkoutData = checkoutDataJSON && JSON.parse(checkoutDataJSON);
                const order = orderJSON && JSON.parse(orderJSON);

                const {grandTotal = 0, shippingAmount = 0, taxAmount = 0} = orderLastData;
                const calcRevenue = grandTotal - taxAmount - shippingAmount;
                const revenue =
                    checkoutData?.paymentMethod === 'ugiftcert' && calcRevenue < 0
                        ? '0'
                        : convertPriceEEC(calcRevenue);

                const isErrored = logWithSentryForErroneousRevenueOrOrder(order);

                if (isErrored) {
                    resolve();
                    return;
                }

                sendEvent({
                    checkoutData,
                    id,
                    order,
                    orderLastData,
                    placeOfInteraction,
                    revenue,
                    router,
                });

                resolve();
            } catch (error) {
                logWithSentry(error);
            }
        });
    }, [id, orderLastData, router]);

    return {
        isInsurance: insurance,
        sendEventForInsuranceOrder,
        sendEventForRegularOrder,
    };
};
